import { computed, effect, Injectable, signal } from '@angular/core';

import { injectTellentClientInternalRoutes, TellentRoute } from '@core/tellent-types';
import { injectTellentHostUrlBuilder } from '@core/tellent-util';

type NavigationCommand = { id: string; params?: Record<string, string | number> };

/**
 * @desc
 * The purpose of this service is to handle navigation within the Tellent Client compatible application.
 * It provides a way to register navigation routes, handle navigation events, and manage the current location.
 * It's exposed publicly so that host applications can register their own (recognized) navigation routes and handle navigation events.
 */
@Injectable({
  providedIn: 'root',
})
export class TellentNavigationRoutingService {
  #tellentHostUrlBuilder = injectTellentHostUrlBuilder();

  public location = signal<string>('');
  public hostAppRoutes = signal<TellentRoute[]>([]);
  public internalRoutes = injectTellentClientInternalRoutes({ optional: true }) ?? [];
  public recognizedRoutes = computed(() => [...this.hostAppRoutes(), ...this.internalRoutes]);

  public possibleMatch = computed(() => {
    const routes = this.recognizedRoutes();
    const location = this.location();
    return routes.find(route => route.matchPattern(location));
  });

  private navigationHandler: (url: string) => void = this.noopNavLinkHandler;

  private internalRoutesStack = new Set<{ route: any; ref: any }>();

  constructor() {
    effect(() => {
      const location = this.location();
      const route = this.internalRoutes.find(route => route.matchPattern(location));

      if (route && route.handler) {
        const alreadyOpened = Array.from(this.internalRoutesStack).find(
          internal => internal.route.id === route.id,
        );

        if (alreadyOpened) return;

        this.internalRoutesStack.add({
          route,
          ref: route.handler.trigger(location, previousUrl => this.navigate(previousUrl)),
        });
      }

      this.internalRoutesStack.forEach(internal => {
        if (!internal.route.matchPattern(location)) {
          internal.route.handler.dispose(internal.ref);
          this.internalRoutesStack.delete(internal);
        }
      });
    });
  }
  /**
   * @desc
   * Delegates navigation to a custom handler.
   * Host app should register a handler to handle navigation.
   * Handler is called with the URL to navigate to, when `navigate` method is called.
   * Currently used by `TellentNavigationComponent` to notify the host app about navigation events.
   * @param handler
   */
  public registerNavHandler(handler: (url: string) => void): void {
    this.navigationHandler = handler;
  }

  /**
   * @desc
   * Navigates to the specified URL using the registered navigation handler.
   * @param url
   * @param queryParams
   */
  public navigate(url: string) {
    this.navigationHandler(url);
  }

  public navigateById(to: NavigationCommand) {
    const path = this.buildPath(to);
    if (path) this.navigationHandler(path);
  }

  public buildPath(to: NavigationCommand) {
    const route = this.recognizedRoutes().find(route => to.id === route.id);

    if (route) {
      return this.#tellentHostUrlBuilder(route, to.params);
    } else {
      console.warn(`Route ${to.id} not found`);
      return;
    }
  }

  private noopNavLinkHandler() {
    console.warn(
      `[Tellent Client] Noop redirect handler called, register custom handler with "registerHandler" method.`,
    );
  }
}
